import React from 'react';
import { useNavigate } from 'react-router-dom';

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DescriptionIcon from '@mui/icons-material/Description';
import InventoryIcon from '@mui/icons-material/Inventory';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import PeopleIcon from '@mui/icons-material/People';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SchoolIcon from '@mui/icons-material/School';
import SearchIcon from '@mui/icons-material/Search';
import SecurityIcon from '@mui/icons-material/Security';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useAuth } from 'contexts/AuthContext';

import RoleChip from 'components/chip/RoleChip';
import { ScreenKey } from 'constant/ScreenPermission';
import { Role } from 'stores/AuthenticationStoreAdapter';

type UseCaseItem = {
  segment: string;
  title: string;
  description: string;
};

type FeatureSection = {
  key: string;
  basePath: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
  roles: Role[];
  allUsers?: boolean;
  useCases: UseCaseItem[];
};

const featureSections: FeatureSection[] = [
  {
    key: 'user',
    basePath: '/guide/user',
    icon: <AdminPanelSettingsIcon />,
    title: 'ユーザー管理',
    description: 'ユーザーの登録・編集・削除・権限設定を行います',
    color: '#26A69A',
    roles: ['ADMIN'],
    useCases: [
      {
        segment: 'register',
        title: '新規ユーザーを登録して権限を付与する',
        description: '新しいユーザーアカウントを作成し、適切な権限を設定します。',
      },
      {
        segment: 'update',
        title: 'ユーザー情報を変更する',
        description: '既存ユーザーのメールアドレス・ユーザー名・パスワードを変更します。',
      },
      {
        segment: 'delete',
        title: 'ユーザーを削除する',
        description: '不要になったユーザーアカウントを削除します。',
      },
      {
        segment: 'role',
        title: 'ユーザーの権限を変更する',
        description: '既存ユーザーに付与されている権限を変更します。',
      },
      {
        segment: 'search',
        title: 'ユーザーを検索する',
        description: '条件を指定してユーザーを検索し、一覧を確認します。',
      },
    ],
  },
  {
    key: 'notice',
    basePath: '/guide/notice',
    icon: <NotificationsOutlinedIcon />,
    title: '通知管理',
    description: '通知・お知らせの作成・管理・確認を行います',
    color: '#FF7043',
    roles: ['ADMIN', 'BACK_OFFICE', 'MANAGER'],
    useCases: [
      {
        segment: 'create-notice',
        title: '特定ユーザーに通知を送る',
        description: '特定のユーザーに対して個人向け通知（NOTICE）を配信します。',
      },
      {
        segment: 'create-info',
        title: '全ユーザーにお知らせを配信する',
        description:
          '全ユーザーに対してお知らせ（INFO）または重要なお知らせ（IMPORTANT）を配信します。',
      },
      {
        segment: 'admin-manage',
        title: '通知を管理する（管理者）',
        description: '通知の修正、削除、一括解決、検索を行います。',
      },
      {
        segment: 'user',
        title: '自分宛ての通知を確認する',
        description: '自分宛ての通知の確認、個人通知の削除、お知らせの解決を行います。',
      },
    ],
  },
  {
    key: 'financial-year',
    basePath: '/guide/financial-year',
    icon: <CalendarMonthIcon />,
    title: '年度管理',
    description: '年度データの登録と年度選択機能について',
    color: '#8D6E63',
    roles: ['ADMIN'],
    useCases: [
      {
        segment: 'register',
        title: '次年度を登録する',
        description: '年度データの登録と年度選択機能について説明します。',
      },
    ],
  },
  {
    key: 'partner',
    basePath: '/guide/partner',
    icon: <PeopleIcon />,
    title: 'パートナー管理',
    description: 'パートナー企業の登録・更新・検索を行います',
    color: '#42A5F5',
    roles: ['ADMIN', 'BACK_OFFICE', 'MANAGER'],
    useCases: [
      {
        segment: 'register',
        title: '新規パートナーを登録する',
        description: '新しいパートナー企業（法人/個人事業主）を登録します。',
      },
      {
        segment: 'bulk-register',
        title: 'パートナーを一括登録する',
        description: 'CSVファイルを使用して複数のパートナーを一括で登録します。',
      },
      {
        segment: 'update',
        title: 'パートナー情報を更新する',
        description: '既存パートナーの情報を変更します（住所変更、契約終了等）。',
      },
      {
        segment: 'detail',
        title: 'パートナーの詳細情報を確認する',
        description: 'パートナーの登録内容を詳細に確認します。',
      },
      {
        segment: 'search',
        title: 'パートナーを検索する',
        description: '条件を指定してパートナーを絞り込み検索します。',
      },
    ],
  },
  {
    key: 'client',
    basePath: '/guide/client',
    icon: <PeopleIcon />,
    title: '取引先管理',
    description: '取引先企業の登録・更新・検索を行います',
    color: '#66BB6A',
    roles: ['ADMIN', 'BACK_OFFICE', 'MANAGER'],
    useCases: [
      {
        segment: 'register',
        title: '新規取引先を登録する',
        description: '新しい取引先企業を登録します。',
      },
      {
        segment: 'bulk-register',
        title: '取引先を一括登録する',
        description: 'CSVファイルを使用して複数の取引先を一括で登録します。',
      },
      {
        segment: 'update',
        title: '取引先情報を更新する',
        description: '既存取引先の情報を変更します（住所変更、取引終了等）。',
      },
      {
        segment: 'detail',
        title: '取引先の詳細情報を確認する',
        description: '取引先の登録内容を詳細に確認します。',
      },
      {
        segment: 'search',
        title: '取引先を検索する',
        description: '条件を指定して取引先を絞り込み検索します。',
      },
    ],
  },
  {
    key: 'project',
    basePath: '/guide/project',
    icon: <PeopleIcon />,
    title: 'プロジェクト管理',
    description: 'プロジェクトの登録・更新・ステータス管理を行います',
    color: '#FFA726',
    roles: ['ADMIN', 'BACK_OFFICE', 'MANAGER'],
    useCases: [
      {
        segment: 'register',
        title: '新規プロジェクトを登録する',
        description: '新しいプロジェクトを登録し、取引先・マネージャーと紐付けます。',
      },
      {
        segment: 'bulk-register',
        title: 'プロジェクトを一括登録する',
        description: 'CSVファイルを使用して複数のプロジェクトを一括で登録します。',
      },
      {
        segment: 'update',
        title: 'プロジェクト情報を更新する',
        description: '既存プロジェクトの情報を変更します（ステータス変更、情報修正等）。',
      },
      {
        segment: 'detail',
        title: 'プロジェクトの詳細情報を確認する',
        description: 'プロジェクトの登録内容を詳細に確認します（取引先・マネージャー情報含む）。',
      },
      {
        segment: 'search',
        title: 'プロジェクトを検索する',
        description: '条件を指定してプロジェクトを絞り込み検索します。',
      },
    ],
  },
  {
    key: 'member',
    basePath: '/guide/member',
    icon: <PeopleIcon />,
    title: '要員管理',
    description: '社員・社外要員の登録・更新・削除を行います',
    color: '#AB47BC',
    roles: ['ADMIN', 'BACK_OFFICE', 'MANAGER'],
    useCases: [
      {
        segment: 'register-employee',
        title: '社員を登録する',
        description: '自社の社員を要員として登録します。',
      },
      {
        segment: 'register-external',
        title: '社外要員を登録する',
        description: 'パートナー企業に所属する社外要員を登録します。',
      },
      {
        segment: 'bulk-register',
        title: '要員を一括登録する',
        description: 'CSVファイルを使用して複数の要員を一括で登録します。',
      },
      {
        segment: 'update',
        title: '要員情報を更新する',
        description: '既存要員の情報を変更します。',
      },
      {
        segment: 'delete',
        title: '要員を削除する',
        description: '不要になった要員を削除します（退職・契約終了等）。',
      },
      {
        segment: 'search',
        title: '要員を検索する',
        description: '条件を指定して要員を絞り込み検索します。',
      },
      {
        segment: 'detail',
        title: '要員の詳細情報を確認する',
        description: '要員の登録内容を詳細に確認します。',
      },
    ],
  },
  {
    key: 'contract',
    basePath: '/guide/contract',
    icon: <DescriptionIcon />,
    title: '契約管理',
    description: '契約の申請・承認・変更・解除と契約情報の管理を行います',
    color: '#EC407A',
    roles: ['ADMIN', 'BACK_OFFICE', 'MANAGER'],
    useCases: [
      {
        segment: 'new-request',
        title: '新規契約を申請する',
        description: '新しい要員の契約を申請し、承認プロセスを開始します。',
      },
      {
        segment: 'change-terminate',
        title: '契約の変更・解除を申請する',
        description: '稼働中の契約条件を変更する、または契約を終了します。',
      },
      {
        segment: 'request-manage',
        title: '契約申請を修正・対応・取消する',
        description: '申請内容の修正と再申請、管理部門による対応、申請の取消を行います。',
      },
      {
        segment: 'comment',
        title: '契約申請にコメントする',
        description: '契約申請に関する質問・連絡をコメントで行います。',
      },
      {
        segment: 'duplicate',
        title: '契約申請を複製する',
        description: '既存の申請内容をコピーし、メンバーだけ変更して一括作成します。',
      },
      {
        segment: 'paste',
        title: 'ペースト定義・スプレッドシートコピー',
        description:
          '契約情報をGoogleスプレッドシートに貼り付けるためのカラム構成定義とコピー操作です。',
      },
      {
        segment: 'search',
        title: '契約を検索・詳細確認する',
        description: '契約一覧と契約申請一覧の検索、契約詳細と履歴の確認方法です。',
      },
      {
        segment: 'checklist',
        title: 'チェックリストを管理する',
        description: '契約申請のチェックリスト操作と、チェックリスト項目の管理方法です。',
      },
      {
        segment: 'template',
        title: 'テンプレート（自由項目）を管理する',
        description:
          '契約申請フォームに表示される動的項目（テンプレート）を追加・編集・並び替えします。',
      },
    ],
  },
  {
    key: 'assessment',
    basePath: '/guide/assessment',
    icon: <InventoryIcon />,
    title: '資産管理',
    description: 'PC等の資産の登録・更新・除却を行います',
    color: '#7E57C2',
    roles: ['ADMIN', 'BACK_OFFICE'],
    useCases: [
      {
        segment: 'register',
        title: '資産を登録する',
        description: '新しいPC等の資産を台帳に登録します。',
      },
      {
        segment: 'bulk-register',
        title: '資産を一括登録する',
        description: 'CSVファイルを使用して複数の資産を一括で登録します。',
      },
      {
        segment: 'update',
        title: '資産情報を更新する',
        description: '資産の利用者・管理者・管理番号等の情報を変更します。',
      },
      {
        segment: 'dispose',
        title: '資産を除却する',
        description: '廃棄・故障等で不要になった資産を除却（論理削除）します。',
      },
      {
        segment: 'detail',
        title: '資産の詳細情報を確認する',
        description: '資産の登録内容を詳細に確認します。',
      },
      {
        segment: 'search',
        title: '資産を検索する',
        description: '条件を指定して資産を絞り込み検索します。',
      },
    ],
  },
  {
    key: 'security',
    basePath: '/guide/security',
    icon: <SecurityIcon />,
    title: 'セキュリティチェック',
    description: '月次セキュリティチェックの管理・実施を行います',
    color: '#78909C',
    roles: [],
    allUsers: true,
    useCases: [
      {
        segment: 'admin-start',
        title: 'セキュリティチェックを管理する（管理者）',
        description: 'セキュリティチェックの開始・実施状況確認・リマインド送信を行います。',
      },
      {
        segment: 'user-execute',
        title: 'セキュリティチェックを実施する（ユーザー）',
        description: '月次セキュリティチェックとPCパッチ確認を実施します。',
      },
    ],
  },
  {
    key: 'training',
    basePath: '/guide/training',
    icon: <SchoolIcon />,
    title: 'ワークスペース（コードエディタ）',
    description: 'VSCode ベースのコードエディタでワークスペースを操作・コードを実行します',
    color: '#00838F',
    roles: [],
    allUsers: true,
    useCases: [
      {
        segment: 'overview',
        title: '概要',
        description: 'ワークスペース（コードエディタ）機能の概要・権限・画面構成を説明します。',
      },
      {
        segment: 'workspace',
        title: 'ワークスペースを操作する',
        description: 'フォルダ・ファイルの作成/リネーム/削除、編集・保存を行います。',
      },
      {
        segment: 'execute',
        title: 'コードを実行して履歴を確認する',
        description: 'コードを実行し、実行結果と履歴の確認・リバートを行います。',
      },
      {
        segment: 'admin',
        title: '他ユーザーのワークスペースを閲覧する（管理者）',
        description: '指定したユーザーのワークスペースを閲覧・操作します。',
      },
    ],
  },
  {
    key: 'common',
    basePath: '/guide/common',
    icon: <SearchIcon />,
    title: '共通機能',
    description: '検索条件の保存・フィードバック送信など共通的な機能',
    color: '#5C6BC0',
    roles: [],
    allUsers: true,
    useCases: [
      {
        segment: 'search-condition',
        title: '検索条件を保存・呼び出す',
        description: '各管理画面の検索条件入力・保存・呼び出し・削除の共通操作を説明します。',
      },
      {
        segment: 'feedback',
        title: '不具合報告・フィードバックを送る',
        description:
          'ツールバーの設定メニューから開発者に不具合報告やフィードバックを送信できます。',
      },
    ],
  },
];

const GuideHome: React.FC = () => {
  const navigate = useNavigate();
  const { userSession } = useAuth();
  const isDeveloper = userSession.allowedScreens.includes(ScreenKey.GUIDE_DEPLOY);

  return (
    <Stack spacing={4} sx={{ maxWidth: 960 }}>
      {/* ヒーローセクション */}
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 3,
          p: 4,
          bgcolor: 'action.hover',
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h5" fontWeight={700}>
            Ardito Portal - Kanzhong - 利用ガイド
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
            KanzhongはArdito
            社内のバックオフィス業務を一元管理するためのシステムです。要員管理・パートナー管理・契約管理・資産管理・セキュリティチェックなど、日々の業務を効率的に運用できます。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
            Kanzhong
            の名は、中国・春秋時代の斉国の宰相・管仲に由来します。内政改革により国力を飛躍的に高めた名宰相の行政手腕に倣い、社内業務を効率的に統治することを目指しています。
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Chip label="要員管理" size="small" variant="outlined" />
            <Chip label="契約管理" size="small" variant="outlined" />
            <Chip label="資産管理" size="small" variant="outlined" />
            <Chip label="セキュリティ" size="small" variant="outlined" />
          </Stack>
        </Stack>
      </Box>

      {/* システム概要 */}
      <Stack spacing={2}>
        <Typography variant="subtitle1" fontWeight={600}>
          システム概要
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <OverviewCard
              title="ロールベースアクセス制御"
              description="管理者 / 課長 / 管理部門 / 一般 / 開発者 の5つのロールで画面アクセスを制御します。"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <OverviewCard
              title="検索・一覧・詳細の3ペイン構成"
              description="多くの管理画面は検索条件パネル・一覧パネル・詳細パネルの3ペイン構成で操作します。"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <OverviewCard
              title="通知・お知らせ機能"
              description="個人向け通知や全体へのお知らせを配信。ヘッダーの通知バッジから確認できます。"
            />
          </Grid>
        </Grid>
      </Stack>

      <Divider />

      {/* 機能別ガイド */}
      <Stack spacing={2}>
        <Typography variant="subtitle1" fontWeight={600}>
          機能別ガイド
        </Typography>
        <Typography variant="body2" color="text.secondary">
          各機能の具体的な操作手順を確認できます。知りたい機能を選択してください。
        </Typography>
        <Stack spacing={2}>
          {featureSections.map((section) => (
            <FeatureTile key={section.key} section={section} onNavigate={navigate} />
          ))}
        </Stack>
      </Stack>

      {/* 開発者向け（DEVELOPER限定） */}
      {isDeveloper && (
        <>
          <Divider />
          <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight={600}>
              開発者向け
            </Typography>
            <Typography variant="body2" color="text.secondary">
              デプロイ手順など、開発者向けの情報です。
            </Typography>
            <Stack spacing={2}>
              <FeatureTile
                section={{
                  key: 'deploy',
                  basePath: '/guide/deploy',
                  icon: <RocketLaunchIcon />,
                  title: 'デプロイ',
                  description: 'フロントエンド・バックエンドの手動デプロイ手順',
                  color: '#546E7A',
                  roles: ['DEVELOPER'],
                  useCases: [
                    {
                      segment: 'overview',
                      title: 'デプロイ概要',
                      description: 'システム構成とデプロイ方式の全体像を確認します。',
                    },
                    {
                      segment: 'frontend',
                      title: 'フロントエンド手動デプロイ',
                      description: 'S3 + CloudFront への手動デプロイ手順です。',
                    },
                    {
                      segment: 'backend',
                      title: 'バックエンド手動デプロイ',
                      description: 'ECR + ECS Fargate への手動デプロイ手順です。',
                    },
                    {
                      segment: 'db-access',
                      title: '踏み台経由DB接続',
                      description: 'SSMを使用して踏み台サーバー経由で本番DBに接続する手順です。',
                    },
                  ],
                }}
                onNavigate={navigate}
              />
            </Stack>
          </Stack>
        </>
      )}
    </Stack>
  );
};

type FeatureTileProps = {
  section: FeatureSection;
  onNavigate: (path: string) => void;
};

const FeatureTile: React.FC<FeatureTileProps> = ({ section, onNavigate }) => {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* 機能ヘッダー */}
      <ButtonBase
        onClick={() => onNavigate(`${section.basePath}/overview`)}
        sx={{
          width: '100%',
          display: 'block',
          textAlign: 'left',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          transition: 'background-color 0.2s',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${section.color}14`,
              color: section.color,
            }}
          >
            {React.cloneElement(section.icon as React.ReactElement<{ sx?: object }>, {
              sx: { fontSize: 20 },
            })}
          </Box>
          <Stack spacing={0.25} sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              {section.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {section.description}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {section.allUsers ? (
              <Chip
                label="全ユーザー"
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
              />
            ) : (
              section.roles.map((role) => <RoleChip key={role} role={role} size="small" />)
            )}
          </Stack>
        </Stack>
      </ButtonBase>

      {/* ユースケースカード一覧 */}
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {section.useCases.map((useCase) => (
            <Grid key={useCase.segment} size={{ xs: 12, sm: 6, md: 4 }}>
              <ButtonBase
                onClick={() => onNavigate(`${section.basePath}/${useCase.segment}`)}
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'block',
                  textAlign: 'left',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  transition: 'border-color 0.2s',
                  '&:hover': { borderColor: section.color },
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: `${section.color}14`,
                        color: section.color,
                      }}
                    >
                      {React.cloneElement(section.icon as React.ReactElement<{ sx?: object }>, {
                        sx: { fontSize: 16 },
                      })}
                    </Box>
                    <ArrowForwardIosIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                  </Stack>
                  <Stack spacing={0.5}>
                    <Typography variant="body2" fontWeight={600}>
                      {useCase.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {useCase.description}
                    </Typography>
                  </Stack>
                </Stack>
              </ButtonBase>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

type OverviewCardProps = {
  title: string;
  description: string;
};

const OverviewCard: React.FC<OverviewCardProps> = ({ title, description }) => (
  <Box
    sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 2,
      p: 2,
      height: '100%',
    }}
  >
    <Stack spacing={1}>
      <Typography variant="body2" fontWeight={600}>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {description}
      </Typography>
    </Stack>
  </Box>
);

export default GuideHome;
